import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../../../lib/http/errors";
import type { Visibility } from "../../../lib/api/fieldApi";
import { useFieldApi } from "../../../hooks/useFieldApi";
import { Button } from "../../../components/ui/Button";
import { ErrorBanner, Loading, ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { hasMessage, useT } from "../../../i18n";
import { defaultTemplates, minutesOf, SCRIPT_TEMPLATE_KEYS } from "../../room";
import type { ScriptTemplate, ScriptTemplateKey } from "../../room";
import { InvalidField } from "../../teacher-settings";
import { DeepLinkUsed, useDeepLinkApi } from "../api/deeplinkApi";
import type { DeepLinkBody, DeepLinkOptions } from "../types";
import { SegGroup } from "../../../components/ui/Seg";
import { TIME_OPTIONS_S } from "../../teacher-settings";
import "../../../styles/teacher.css";

const VIS: readonly Visibility[] = ["class", "selected", "private"];
export const TITLE_MAX = 60;

/** 템플릿 단계 초를 시간 예산에 맞춘다(계약 §2.3 — 서버 템플릿은 10분 기준 · 최소 30초) */
export function scaleTemplate(t: ScriptTemplate, budgetS: number, baseS = 600): ScriptTemplate {
  return {
    ...t,
    steps: t.steps.map((s) => ({
      ...s,
      seconds: Math.max(30, Math.round((s.seconds * budgetS) / baseS)),
    })),
  };
}

/** 폼을 LMS 로 자동 제출(시험은 바꾼다 — jsdom 에는 form.submit 이 없다) */
let submitForm = (f: HTMLFormElement) => f.submit();
export function setDeepLinkSubmitForTests(fn: ((f: HTMLFormElement) => void) | null) {
  submitForm = fn ?? ((f) => f.submit());
}

/** datetime-local 값(현지) → ISO(UTC) · 비었으면 null */
export function toIso(local: string): string | null {
  if (!local) return null;
  const ms = Date.parse(local);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

/**
 * 마무리 활동 만들기 — LTI Deep Linking(SR-INT-01 · 계약 §2.1) · /teacher/deeplink(#ticket= 로 들어와 세션이 선 뒤 연다).
 * FS-11-05 코스 설정 폼(모드(추천) · 기본 코스 · 마무리 시간 · 공개 범위 · 자동 완료 제안)에 이름 · 마무리 템플릿 · 예정 시각을 더했다.
 *  - 제출: POST …/teacher/deeplink → { returnUrl, jwt } → 숨은 폼(JWT)을 LMS 돌아가는 주소로 자동 POST(LtiDeepLinkingResponse)
 *    (셸에는 CSP 가 없다 — 나중에 CSP 를 두면 form-action 에 LMS 돌아가는 주소(http/https)를 열어야 한다)
 *  - 422 { field } → 그 칸 표시 + 오류 배너 · 409 = Deep Linking 실행이 아니거나 이미 돌려보냄(실행마다 한 번)
 *  - 교사 화면이라 자유 텍스트(이름)가 있다(학생 화면 아님 — DR-02 밖)
 * 활동은 Campus 모듈에서 이 폼으로만 만든다 — 교사 홈 활동 표는 열기·설정·현황만(와이어 FS-01-04 안내 문구).
 */
export function DeepLinkScreen() {
  const t = useT();
  const id = useId();
  const navigate = useNavigate();
  const api = useDeepLinkApi();
  const field = useFieldApi();
  const [opts, setOpts] = useState<DeepLinkOptions | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [course, setCourse] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [modeId, setModeId] = useState<string | null>(null);
  const [presetKey, setPresetKey] = useState<string | null>("medium");
  const [budget, setBudget] = useState(600);
  const [vis, setVis] = useState<Visibility>("class");
  const [auto, setAuto] = useState(false);
  const [template, setTemplate] = useState<ScriptTemplateKey>("view");
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState(false);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [used, setUsed] = useState(false);
  const [post, setPost] = useState<{ returnUrl: string; jwt: string } | null>(null);
  const form = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!api) return;
    let off = false;
    api
      .options()
      .then((o) => {
        if (off) return;
        setOpts(o);
        setModeId(o.recommendedModeId ?? o.modes[0]?.id ?? null);
        setBudget(o.defaults.timeBudgetS);
        setVis(o.defaults.visibility);
        setAuto(o.defaults.autoComplete);
      })
      .catch((e: unknown) => !off && setLoadErr(e instanceof ApiError ? e.code : "NET-SERVER"));
    return () => {
      off = true;
    };
  }, [api]);
  useEffect(() => {
    if (!field) return;
    let off = false;
    field
      .teacherOverview()
      .then((d) => !off && setCourse(d?.courseLabel ?? null))
      .catch(() => {});
    return () => {
      off = true;
    };
  }, [field]);

  // 응답을 받으면 LMS 로 자동 POST
  useEffect(() => {
    if (post && form.current) submitForm(form.current);
  }, [post]);

  useTopBar({
    icon: "🔗",
    title: t("deeplink.topTitle"),
    chips: course ? [{ label: course, testId: "course-chip" }] : [],
  });

  const modeLabel = (m: string) =>
    hasMessage(`deeplink.mode.${m}`)
      ? t(`deeplink.mode.${m}`)
      : hasMessage(`teacher.room.modeName.${m}`)
        ? t(`teacher.room.modeName.${m}`)
        : m;
  const presetLabel = (k: string) =>
    hasMessage(`deeplink.preset.${k}`)
      ? t(`deeplink.preset.${k}`)
      : hasMessage(`course.preset.${k}`)
        ? t(`course.preset.${k}`)
        : k;
  const templates = (opts?.templates.length ? opts.templates : defaultTemplates()).map((x) =>
    scaleTemplate(x, budget),
  );
  // 고른 모드의 프리셋(없으면 칸을 숨기고 보내지 않는다) — 모드를 바꾸면 그 모드의 첫 프리셋(보통이 있으면 보통)
  const modePresets = (modeId && opts?.presets[modeId]) || [];
  const coursePreset = modePresets.length > 0;
  const preset =
    presetKey && modePresets.includes(presetKey)
      ? presetKey
      : modePresets.includes("medium")
        ? "medium"
        : (modePresets[0] ?? null);
  const bad = (f: string) => (invalid === f ? true : undefined);
  const titleOk = title.trim().length >= 1 && title.trim().length <= TITLE_MAX;

  const submit = async () => {
    if (!api || !modeId || !titleOk) {
      if (!titleOk) setInvalid("title");
      return;
    }
    setBusy(true);
    setInvalid(null);
    setFailure(null);
    const body: DeepLinkBody = {
      title: title.trim(),
      modeId,
      ...(coursePreset ? { presetKey: preset } : {}),
      timeBudgetS: budget,
      visibility: vis,
      autoComplete: auto,
      scriptTemplate: template,
      scheduledAt: toIso(when),
    };
    try {
      setPost(await api.create(body));
    } catch (e) {
      if (e instanceof InvalidField) setInvalid(e.field ?? "");
      else if (e instanceof DeepLinkUsed) setUsed(true);
      else setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
      setBusy(false);
    }
  };

  return (
    <ScreenRoot
      id="FS-11-05-DL"
      title={t("deeplink.title")}
      hideTitle
      className="pf-teacher pf-deeplink"
    >
      {loadErr ? (
        <ErrorBanner
          cause={t("deeplink.loadFailed")}
          action={t("deeplink.loadNext")}
          code={loadErr}
        />
      ) : !opts ? (
        <Loading />
      ) : (
        <form
          className="pf-settings"
          data-testid="deeplink-form"
          aria-busy={busy || undefined}
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="pf-settings__top">
            <section className="pf-pane pf-pane--hero" aria-labelledby={`${id}-act`}>
              <h2 id={`${id}-act`} className="pf-h3">
                {t("deeplink.activity")}
              </h2>
              <div className="pf-field-row">
                <label className="pf-field-label" htmlFor={`${id}-title`}>
                  {t("deeplink.name")}
                </label>
                <input
                  id={`${id}-title`}
                  className="pf-input pf-deeplink__title"
                  value={title}
                  maxLength={TITLE_MAX}
                  aria-required="true"
                  placeholder={t("deeplink.namePlaceholder")}
                  aria-invalid={bad("title")}
                  data-testid="deeplink-title"
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setInvalid(null);
                  }}
                />
                <span className="pf-counter">
                  {title.length}/{TITLE_MAX}
                </span>
              </div>
              <div className="pf-field-row">
                <label className="pf-field-label" htmlFor={`${id}-when`}>
                  {t("deeplink.when")}
                </label>
                <input
                  id={`${id}-when`}
                  type="datetime-local"
                  className="pf-input"
                  value={when}
                  aria-invalid={bad("scheduledAt")}
                  data-testid="deeplink-when"
                  onChange={(e) => setWhen(e.target.value)}
                />
                <span className="pf-note">{t("deeplink.whenNote")}</span>
              </div>
              <SegGroup
                label={t("deeplink.modeLabel")}
                testId="deeplink-mode"
                invalid={bad("modeId")}
              >
                {opts.modes.map((m) => (
                  <label key={m.id} className="pf-seg__opt">
                    <input
                      type="radio"
                      name={`${id}-mode`}
                      value={m.id}
                      checked={modeId === m.id}
                      onChange={() => setModeId(m.id)}
                    />
                    <span>
                      {modeLabel(m.id)}
                      {m.recommended && (
                        <span className="pf-chip-static pf-chip-static--ok">
                          {t("teacher.room.recommended")}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </SegGroup>
              {coursePreset && (
                <SegGroup
                  label={t("teacher.settings.preset")}
                  testId="deeplink-preset"
                  invalid={bad("presetKey")}
                >
                  {modePresets.map((k) => (
                    <label key={k} className="pf-seg__opt">
                      <input
                        type="radio"
                        name={`${id}-preset`}
                        value={k}
                        checked={preset === k}
                        onChange={() => setPresetKey(k)}
                      />
                      <span>{presetLabel(k)}</span>
                    </label>
                  ))}
                </SegGroup>
              )}
            </section>

            <section className="pf-pane" aria-labelledby={`${id}-set`}>
              <h2 id={`${id}-set`} className="pf-h3">
                {t("deeplink.settings")}
              </h2>
              <SegGroup
                label={t("teacher.settings.time")}
                testId="deeplink-time"
                invalid={bad("timeBudgetS")}
              >
                {TIME_OPTIONS_S.map((s) => (
                  <label key={s} className="pf-seg__opt">
                    <input
                      type="radio"
                      name={`${id}-time`}
                      value={s}
                      checked={budget === s}
                      onChange={() => setBudget(s)}
                    />
                    <span>{t("teacher.home.minutes", { n: s / 60 })}</span>
                  </label>
                ))}
              </SegGroup>
              <SegGroup
                label={t("teacher.settings.visTitle")}
                variant="pill"
                testId="deeplink-visibility"
                invalid={bad("visibility")}
              >
                {VIS.map((v) => (
                  <label key={v} className="pf-seg__opt">
                    <input
                      type="radio"
                      name={`${id}-vis`}
                      value={v}
                      checked={vis === v}
                      onChange={() => setVis(v)}
                    />
                    <span>{t(`teacher.settings.vis.${v}`)}</span>
                  </label>
                ))}
              </SegGroup>
              <SegGroup
                label={t("teacher.settings.autoComplete")}
                testId="deeplink-auto"
                variant="green"
              >
                {[false, true].map((on) => (
                  <label key={String(on)} className="pf-seg__opt">
                    <input
                      type="radio"
                      name={`${id}-auto`}
                      checked={auto === on}
                      onChange={() => setAuto(on)}
                    />
                    <span>{t(on ? "deeplink.autoOn" : "deeplink.autoOff")}</span>
                  </label>
                ))}
              </SegGroup>
              <p className="pf-note">{t("deeplink.settingsNote")}</p>
            </section>
          </div>

          <section className="pf-pane" aria-labelledby={`${id}-tpl`}>
            <h2 id={`${id}-tpl`} className="pf-h3">
              {t("deeplink.template")}
            </h2>
            <div
              className="pf-deeplink__templates"
              role="radiogroup"
              aria-labelledby={`${id}-tpl`}
              aria-invalid={bad("scriptTemplate")}
              data-testid="deeplink-templates"
            >
              {SCRIPT_TEMPLATE_KEYS.map((k) => {
                const tp = templates.find((x) => x.key === k);
                if (!tp) return null;
                return (
                  <label key={k} className="pf-deeplink__tpl" data-template={k}>
                    <input
                      type="radio"
                      name={`${id}-template`}
                      value={k}
                      checked={template === k}
                      onChange={() => setTemplate(k)}
                    />
                    <b>{t(`script.template.${k}`)}</b>
                    <ol className="pf-deeplink__steps">
                      {tp.steps.map((s, i) => (
                        <li key={`${s.key}-${i}`}>
                          <span aria-hidden="true">{s.icon} </span>
                          {t(`script.step.${s.key}`)} ·{" "}
                          {t("teacher.home.minutes", { n: minutesOf(s.seconds) })}
                        </li>
                      ))}
                    </ol>
                  </label>
                );
              })}
            </div>
            <p className="pf-note">{t("deeplink.templateNote")}</p>
          </section>

          {invalid !== null && (
            <ErrorBanner
              cause={t("deeplink.invalid", {
                field: hasMessage(`deeplink.field.${invalid}`)
                  ? t(`deeplink.field.${invalid}`)
                  : invalid || "—",
              })}
              action={t("teacher.settings.invalidNext")}
              code="HTTP-422"
            />
          )}
          {(used || !opts.deepLink) && (
            <ErrorBanner
              cause={t("deeplink.notDeepLink")}
              action={t("deeplink.notDeepLinkNext")}
              code="HTTP-409"
            />
          )}
          {failure && (
            <ErrorBanner
              cause={t("deeplink.failed")}
              action={t("teacher.common.retryLater")}
              code={failure}
            />
          )}
          {post && (
            <p className="pf-muted" role="status" data-testid="deeplink-returning">
              {t("deeplink.returning")}
            </p>
          )}
          <div className="pf-settings__actions">
            <Button
              variant="ghost"
              className="pf-btn--sm"
              disabled={busy}
              onClick={() => navigate("/teacher")}
            >
              {t("teacher.common.cancel")}
            </Button>
            <Button
              type="submit"
              variant="accent"
              className="pf-btn--sm"
              disabled={busy || !modeId || used || !opts.deepLink}
              data-testid="deeplink-submit"
            >
              {busy ? t("deeplink.submitting") : t("deeplink.submit")}
            </Button>
          </div>
        </form>
      )}
      {post && (
        <form
          ref={form}
          method="post"
          action={post.returnUrl}
          className="pf-sr-only"
          data-testid="deeplink-return-form"
          aria-hidden="true"
        >
          <input type="hidden" name="JWT" value={post.jwt} />
        </form>
      )}
    </ScreenRoot>
  );
}
